import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from "@apollo/client/core";
import { useAuthStore } from "@/stores/authStore";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { APP_API_URL } from "@/consts";

const IBG_API_URL = `${APP_API_URL}/graphql`;

// Cache implementation
const cache = new InMemoryCache();

const customFetch = async (uri: any, options: any) => {
  const authStore = useAuthStore();

  // Setting the institution ID
  const url = new URL(uri);
  url.searchParams.append("institution_id", authStore.institutionId);

  const headers = new Headers();

  if (authStore.apiToken) {
    headers.set("Authorization", authStore.apiToken);
  }

  headers.set("IBG-Channel", "manager");

  const response = await window.fetch(url, {
    ...options,
    headers,
  });

  // We should check the pathname first to redirect logout on 401 errors.
  const { pathname } = window.location;
  if (
    response.status === 401 &&
    !pathname.startsWith("/relative-profile-creation")
  ) {
    authStore.logoutPrompt();
    return response;
  }

  if (response.status >= 400) {
    // Errors are reported to Sentry by the caller (GQLClient/API layer) once
    // the error propagates, so we shouldn't also report it here to avoid
    // duplicate Sentry issues for the same failed request.
    throw new Error("GraphQL 4xx error.");
  }

  return response;
};

// Create the graphql client
export const graphqlClient = new ApolloClient({
  cache,
  link: from([
    // Remove the typename from variables
    removeTypenameFromVariables(),
    createHttpLink({
      uri: IBG_API_URL,
      fetch: customFetch,
    }),
  ]),
  defaultOptions: {
    query: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    mutate: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    watchQuery: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  },
  devtools: {
    enabled: false,
  },
});

export default { graphqlClient };
